import axios, { AxiosError, CanceledError, type InternalAxiosRequestConfig } from 'axios'
import { auditInteraction } from './audit'

export const api = axios.create({ baseURL: '/api/v2', timeout: 20000 })

type LockedRequest = InternalAxiosRequestConfig & {
  __actionLockKey?: string
  __ownsActionLock?: boolean
}

const mutationMethods = new Set(['post', 'put', 'patch', 'delete'])
const pendingMutations = new Set<string>()

function actionLockKey(config: LockedRequest) {
  const method = String(config.method || 'get').toLowerCase()
  if (!mutationMethods.has(method)) return ''
  const body = typeof config.data === 'string' ? config.data : JSON.stringify(config.data ?? null)
  return `${method}:${config.baseURL || ''}${config.url || ''}:${body}`
}

function releaseActionLock(config?: LockedRequest) {
  if (config?.__ownsActionLock && config.__actionLockKey) pendingMutations.delete(config.__actionLockKey)
}

api.interceptors.request.use(config => {
  const token = localStorage.getItem('xhs_v2_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  config.headers['X-Audit-Page'] = encodeURIComponent(location.pathname)
  const interaction = auditInteraction()
  if (interaction) config.headers['X-Audit-Interaction'] = interaction

  const locked = config as LockedRequest
  const key = actionLockKey(locked)
  if (key) {
    if (pendingMutations.has(key)) throw new CanceledError('操作正在处理中，请勿重复提交', config)
    pendingMutations.add(key)
    locked.__actionLockKey = key
    locked.__ownsActionLock = true
  }
  return config
})

api.interceptors.response.use(response => {
  releaseActionLock(response.config as LockedRequest)
  if(String(response.config.method).toLowerCase()==='put' && /\/data\/ranking\/month-products\/\d+\/manager/.test(response.config.url||'')) {
    try { localStorage.setItem('xhs-ranking-changed',`${Date.now()}-${Math.random()}`) } catch { /* Same-tab refresh still works without storage. */ }
    window.dispatchEvent(new Event('xhs-ranking-changed'))
  }
  return response.data
}, (error: AxiosError) => {
  releaseActionLock(error.config as LockedRequest | undefined)
  if (error.response?.status === 401) {
    localStorage.removeItem('xhs_v2_token')
    localStorage.removeItem('xhs_v2_user')
    if (location.pathname !== '/login') location.assign('/login')
  }
  const raw = (error.response?.data as { detail?: unknown } | undefined)?.detail
  if (error.response?.status === 403 && (raw as any)?.code === 'PASSWORD_CHANGE_REQUIRED') {
    if (location.pathname !== '/change-password') location.replace('/change-password')
    return Promise.reject(new Error((raw as any).message))
  }
  const detail = Array.isArray(raw)
    ? raw.map(item => (item.loc || []).filter((key: string) => key !== 'body').join('.') + ': ' + (item.msg || '输入无效')).join('；')
    : typeof raw === 'string' ? raw : ''
  return Promise.reject(new Error(detail || error.message || '请求失败'))
})
